import type { AnyID, Item } from '@types'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'
import { buildPouffDragIgnoredObstacleIds } from './pouffArmSnap'
import { buildItemOBBFromMatrix } from './itemObbUtils'
import {
  isPouffDragDebugEnabled,
  isRinconero166Catalog,
  itemCatalogLabel,
  pouffDragLog,
  vec3Label,
} from './pouffDragDiagnostics'

const TMP_DRAGGED_OBB = new OBB()
const TMP_OTHER_OBB = new OBB()
const POUFF_DRAG_MIN_GAP_MM = 1
const TMP_AXIS = new Vector3()
const TMP_CENTER_DIFF = new Vector3()

export type PouffPenetrationHit = {
  otherId: AnyID
  catalogID: string
  isRinconero166: boolean
  snappedSides?: unknown
}

function obbRadiusOnAxis(obb: OBB, axis: Vector3): number {
  const e = obb.rotation.elements
  const hs = obb.halfSize
  return (
    hs.x * Math.abs(axis.x * e[0] + axis.y * e[1] + axis.z * e[2]) +
    hs.y * Math.abs(axis.x * e[3] + axis.y * e[4] + axis.z * e[5]) +
    hs.z * Math.abs(axis.x * e[6] + axis.y * e[7] + axis.z * e[8])
  )
}

function collectObbAxes(obb: OBB, out: Vector3[]): void {
  const e = obb.rotation.elements
  out.push(
    new Vector3(e[0], e[1], e[2]),
    new Vector3(e[3], e[4], e[5]),
    new Vector3(e[6], e[7], e[8]),
  )
}

/** Separación mínima en planta (XZ) entre dos OBB usando SAT. */
function computeObbSeparationPushXZ(dragged: OBB, other: OBB): Vector3 | null {
  const axes: Vector3[] = []
  collectObbAxes(dragged, axes)
  collectObbAxes(other, axes)
  axes.push(new Vector3(1, 0, 0), new Vector3(0, 0, 1))

  let bestPush: Vector3 | null = null
  let bestLen = Infinity

  for (const axis of axes) {
    if (axis.lengthSq() < 1e-10) continue
    TMP_AXIS.copy(axis).normalize()

    const ra = obbRadiusOnAxis(dragged, TMP_AXIS)
    const rb = obbRadiusOnAxis(other, TMP_AXIS)
    TMP_CENTER_DIFF.copy(dragged.center).sub(other.center)
    const distance = Math.abs(TMP_AXIS.dot(TMP_CENTER_DIFF))
    const overlap = ra + rb + POUFF_DRAG_MIN_GAP_MM - distance
    if (overlap <= 0) continue

    const sign = TMP_CENTER_DIFF.dot(TMP_AXIS) >= 0 ? 1 : -1
    const push = TMP_AXIS.clone().multiplyScalar(overlap * sign)
    push.y = 0
    if (push.lengthSq() < 1e-8) continue

    const len = push.length()
    if (len < bestLen) {
      bestLen = len
      bestPush = push
    }
  }

  return bestPush
}

export function getPouffPenetratingObstacles(
  matrix: Matrix4,
  width: number,
  height: number,
  thickness: number,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
  ignoredObstacleIds?: ReadonlySet<string>,
): PouffPenetrationHit[] {
  const dragged = buildItemOBBFromMatrix(matrix, width, height, thickness, TMP_DRAGGED_OBB)
  const hits: PouffPenetrationHit[] = []

  for (const otherId of collisionableIds) {
    if (otherId === excludeId) continue
    if (ignoredObstacleIds?.has(String(otherId))) continue
    const other = items.get(otherId)
    if (!other?.matrix || other.visible === false) continue

    // Reutilizar OBB: evita N allocs por frame (causa de jank en escenas densas).
    buildItemOBBFromMatrix(
      other.matrix,
      other.width,
      other.height,
      other.thickness ?? 0,
      TMP_OTHER_OBB,
    )
    if (!dragged.intersectsOBB(TMP_OTHER_OBB)) continue

    hits.push({
      otherId,
      catalogID: itemCatalogLabel(other),
      isRinconero166: isRinconero166Catalog(other.catalogID),
      snappedSides: (other as Item & { snappedSides?: unknown }).snappedSides,
    })
  }

  return hits
}

function matrixCornerFromMatrix(matrix: Matrix4): Vector3 {
  const pos = new Vector3()
  matrix.decompose(pos, new Quaternion(), new Vector3())
  return pos
}

function ignoredIdsForPouffDrag(
  matrix: Matrix4,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  draggedItem?: Item,
): Set<string> | undefined {
  if (!draggedItem) return undefined
  return buildPouffDragIgnoredObstacleIds(
    matrixCornerFromMatrix(matrix),
    draggedItem,
    items,
    collisionableIds,
  )
}

/**
 * Resuelve la posición de arrastre del puff empujándolo fuera de todas las unidades
 * colisionables con separación OBB real (SAT). Solo traslación en XZ.
 */
export function resolvePouffDragMatrix(
  matrix: Matrix4,
  width: number,
  height: number,
  thickness: number,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
  maxIterations = 24,
  debugLabel?: string,
  draggedItem?: Item,
  explicitIgnoredIds?: ReadonlySet<string>,
): Matrix4 {
  const result = matrix.clone()
  const pos = new Vector3()
  const quat = new Quaternion()
  const scale = new Vector3()
  const startPos = new Vector3()
  matrix.decompose(startPos, new Quaternion(), new Vector3())

  const debug = isPouffDragDebugEnabled()
  const startIgnoredIds = ignoredIdsForPouffDrag(matrix, items, collisionableIds, draggedItem)
  const startPenetrations = getPouffPenetratingObstacles(
    matrix,
    width,
    height,
    thickness,
    items,
    collisionableIds,
    excludeId,
    startIgnoredIds,
  )
  const touches166 = startPenetrations.some((h) => h.isRinconero166)

  if (debug && touches166) {
    pouffDragLog(
      `resolve:start (${debugLabel ?? 'drag'})`,
      {
        proposed: vec3Label(startPos),
        penetrations: startPenetrations,
      },
      { force: true },
    )
  }

  const draggedObb = new OBB()
  const otherObb = new OBB()

  for (let iter = 0; iter < maxIterations; iter++) {
    result.decompose(pos, quat, scale)
    buildItemOBBFromMatrix(result, width, height, thickness, draggedObb)
    const ignoredIds =
      explicitIgnoredIds ??
      ignoredIdsForPouffDrag(result, items, collisionableIds, draggedItem)

    const totalPush = new Vector3()
    let moved = false

    for (const otherId of collisionableIds) {
      if (otherId === excludeId) continue
      if (ignoredIds?.has(String(otherId))) continue
      const other = items.get(otherId)
      if (!other?.matrix || other.visible === false) continue

      buildItemOBBFromMatrix(
        other.matrix,
        other.width,
        other.height,
        other.thickness ?? 0,
        otherObb,
      )
      if (!draggedObb.intersectsOBB(otherObb)) continue

      const push = computeObbSeparationPushXZ(draggedObb, otherObb)
      if (!push || push.lengthSq() === 0) continue

      totalPush.add(push)
      moved = true
    }

    if (!moved) break

    pos.add(totalPush)
    result.setPosition(pos.x, pos.y, pos.z)
  }

  const endPos = new Vector3()
  result.decompose(endPos, new Quaternion(), new Vector3())
  const remaining = getPouffPenetratingObstacles(
    result,
    width,
    height,
    thickness,
    items,
    collisionableIds,
    excludeId,
    explicitIgnoredIds ??
      ignoredIdsForPouffDrag(result, items, collisionableIds, draggedItem),
  )

  if (debug && (touches166 || remaining.length > 0)) {
    pouffDragLog(
      `resolve:end (${debugLabel ?? 'drag'})`,
      {
        from: vec3Label(startPos),
        to: vec3Label(endPos),
        moved: startPos.distanceTo(endPos) > 0.5,
        remainingPenetrations: remaining,
      },
      { force: remaining.length > 0 },
    )
  }

  return result
}

function matrixCornerPosition(matrix: Matrix4): Vector3 {
  const pos = new Vector3()
  matrix.decompose(pos, new Quaternion(), new Vector3())
  return pos
}

function matrixAtPosition(base: Matrix4, x: number, y: number, z: number): Matrix4 {
  const next = base.clone()
  next.setPosition(x, y, z)
  return next
}

export function isPouffMatrixFree(
  matrix: Matrix4,
  width: number,
  height: number,
  thickness: number,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
  draggedItem?: Item,
  explicitIgnoredIds?: ReadonlySet<string>,
): boolean {
  const ignored =
    explicitIgnoredIds ??
    (draggedItem
      ? ignoredIdsForPouffDrag(matrix, items, collisionableIds, draggedItem)
      : undefined)
  return (
    getPouffPenetratingObstacles(
      matrix,
      width,
      height,
      thickness,
      items,
      collisionableIds,
      excludeId,
      ignored,
    ).length === 0
  )
}

/**
 * Desliza el puff desde `fromMatrix` hacia `toMatrix` siguiendo la línea del dedo.
 * Si el dedo apunta al interior de otra unidad, se queda en el último punto libre del rayo.
 */
export function clampPouffDragMatrix(
  fromMatrix: Matrix4,
  toMatrix: Matrix4,
  width: number,
  height: number,
  thickness: number,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
  draggedItem?: Item,
  explicitIgnoredIds?: ReadonlySet<string>,
): Matrix4 {
  const isFree = (candidate: Matrix4) =>
    isPouffMatrixFree(
      candidate,
      width,
      height,
      thickness,
      items,
      collisionableIds,
      excludeId,
      draggedItem,
      explicitIgnoredIds,
    )

  if (isFree(toMatrix)) {
    return toMatrix.clone()
  }

  let safeFrom = fromMatrix.clone()
  if (!isFree(safeFrom)) {
    const fixedFrom = resolvePouffDragMatrix(
      safeFrom,
      width,
      height,
      thickness,
      items,
      collisionableIds,
      excludeId,
      16,
      'clamp-from',
      draggedItem,
    )
    if (isFree(fixedFrom)) {
      safeFrom = fixedFrom
    }
  }

  const fromPos = matrixCornerPosition(safeFrom)
  const toPos = matrixCornerPosition(toMatrix)
  const targetY = toPos.y
  if (fromPos.distanceTo(toPos) < 0.5) {
    return safeFrom
  }

  let bestMatrix = safeFrom.clone()
  let lo = 0
  let hi = 1

  for (let step = 0; step < 16; step++) {
    const mid = (lo + hi) / 2
    const lerped = fromPos.clone().lerp(toPos, mid)
    const candidate = matrixAtPosition(safeFrom, lerped.x, targetY, lerped.z)
    if (isFree(candidate)) {
      bestMatrix = candidate
      lo = mid
    } else {
      hi = mid
    }
  }

  if (isPouffDragDebugEnabled() && lo < 1) {
    const bestPos = matrixCornerPosition(bestMatrix)
    pouffDragLog(
      'clamp-sweep',
      {
        from: vec3Label(fromPos),
        finger: vec3Label(toPos),
        result: vec3Label(bestPos),
        t: Math.round(lo * 1000) / 1000,
      },
      { force: lo < 0.99 },
    )
  }

  return bestMatrix
}

/**
 * Garantiza que el puff no quede dentro de un GLB no permitido.
 * Los IDs en `allowedPenetrationIds` (brazo/padre en ranura exterior) sí se toleran.
 */
export function finalizePouffDragMatrix(
  candidate: Matrix4,
  fallback: Matrix4,
  width: number,
  height: number,
  thickness: number,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
  draggedItem?: Item,
  allowedPenetrationIds?: ReadonlySet<string>,
): Matrix4 {
  const disallowedPenetrations = (matrix: Matrix4) =>
    getPouffPenetratingObstacles(
      matrix,
      width,
      height,
      thickness,
      items,
      collisionableIds,
      excludeId,
    ).filter((hit) => !allowedPenetrationIds?.has(String(hit.otherId)))

  if (disallowedPenetrations(candidate).length === 0) {
    return candidate.clone()
  }

  if (disallowedPenetrations(fallback).length === 0) {
    return fallback.clone()
  }

  const pushed = resolvePouffDragMatrix(
    candidate,
    width,
    height,
    thickness,
    items,
    collisionableIds,
    excludeId,
    20,
    'finalize',
    draggedItem,
    allowedPenetrationIds,
  )
  if (disallowedPenetrations(pushed).length === 0) {
    return pushed
  }

  const pushedFallback = resolvePouffDragMatrix(
    fallback,
    width,
    height,
    thickness,
    items,
    collisionableIds,
    excludeId,
    20,
    'finalize-fallback',
    draggedItem,
    allowedPenetrationIds,
  )
  if (disallowedPenetrations(pushedFallback).length === 0) {
    return pushedFallback
  }

  return fallback.clone()
}

export function strictPouffMatrixPenetratesAny(
  matrix: Matrix4,
  width: number,
  height: number,
  thickness: number,
  items: Map<AnyID, Item>,
  collisionableIds: Set<AnyID>,
  excludeId: AnyID,
): boolean {
  return (
    getPouffPenetratingObstacles(
      matrix,
      width,
      height,
      thickness,
      items,
      collisionableIds,
      excludeId,
    ).length > 0
  )
}
